/*
 * Copyright 2026 Sony Corporation
 */

import { CssBaseline, ThemeProvider } from '@mui/material';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactElement } from 'react';

import type { AppConfig } from './config';
import { AuthProvider } from './contexts/AuthProvider';
import { theme } from './theme';
import { useAuth } from './contexts/auth-context';
import { Login } from './pages/Login';
import { ScenePage } from './pages/ScenePage';
import { WorldEditor } from './pages/WorldEditor';
import { WorldList } from './pages/WorldList';

const RequireAuth = ({ children }: { children: ReactElement }) => {
  const { signedIn } = useAuth();
  const location = useLocation();

  return signedIn ? children : <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
};

export interface AppProps {
  config: AppConfig;
}

export const App = ({ config }: AppProps) => (
  <ThemeProvider theme={theme}>
    <CssBaseline />
    <AuthProvider config={config}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/worlds"
            element={
              <RequireAuth>
                <WorldList />
              </RequireAuth>
            }
          />
          <Route
            path="/worlds/:world_id"
            element={
              <RequireAuth>
                <WorldEditor />
              </RequireAuth>
            }
          />
          <Route
            path="/worlds/:world_id/scenes/:scene_id/edit"
            element={
              <RequireAuth>
                <ScenePage />
              </RequireAuth>
            }
          />
          <Route path="*" element={<Navigate to="/worlds" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  </ThemeProvider>
);
